/* global sessionStorage */

import { useEffect, useState, lazy, Suspense } from 'react'
import { useLayoutVisibility } from '@context/LayoutContext'
import { useMemorama } from '../../context/MemoramaContext'
import { useDeviceType } from '@hooks/useDeviceType'
import { useLanguage } from '@context/LanguageContext'
import { PlayerStatus, ExitGameButton } from '@shared'
import { preloadGameAudio } from '@utils/soundManager'
import PlayersPanel from '../ui/PlayersPanel'
import Board from '../ui/Board'
import OrientationModal from '../ui/OrientationModal'
import styles from './MemoramaGame.module.css'

const ExitGameModal = lazy(() => import('../ui/ExitMemoramaModal'))

function MemoramaGame () {
  useLayoutVisibility({ header: false, footer: false })
  const { resetGame, players, currentTurnIndex, boardSize } = useMemorama()
  const [showExitModal, setShowExitModal] = useState(false)
  const { t } = useLanguage()
  const { isMobile } = useDeviceType()

  // 🔄 Reset orientation hint if board size changes
  useEffect(() => {
    sessionStorage.removeItem('hideOrientationHint')
  }, [boardSize])

  // Preload audio on Game Ready
  useEffect(() => {
    preloadGameAudio()
  }, [])

  const currentPlayer = players[currentTurnIndex]

  // Handlers
  const handleShowExitModal = () => setShowExitModal(true)
  const handleCloseExitModal = () => setShowExitModal(false)
  const handleExitGame = () => {
    resetGame()
    setShowExitModal(false)
  }
  return (
    <section
      className={styles.gameScreen}
      role='region'
      aria-label={t.shared.access.game.area}
    >
      {/* Exit button top right */}
      <ExitGameButton onClick={handleShowExitModal} />

      {/* ℹ️ Orientation hint for tablets in portrait */}
      <OrientationModal />

      {/* 🔵 ARIA-LIVE: turn annoucement */}
      {players.length > 0 && currentPlayer && (
        <div className='sr-only' aria-live='polite'>
          {t.shared.access.player.turnAnnounce.replace('{name}', currentPlayer.name)}
        </div>
      )}
      {/* Player display: Mobile shows current player only, Tablet / Desktop shows all players */}
      {isMobile
        ? (
          <div className={styles.playerStatus}>
            <PlayerStatus
              player={currentPlayer}
              isActive
              playerIndex={currentTurnIndex}
              multiplayerMobile={players.length > 1}
              avatarAlt={t.shared.access.player.avatarAlt.replace('{name}', currentPlayer.name)}
              statSuffix='pts'
              statAriaLabel={`${currentPlayer.score} ${t.shared.access.player.points}`}
            />
          </div>
          )
        : (
          <PlayersPanel />
          )}

      <Board />

      {showExitModal && (
        <Suspense fallback={null}>
          <ExitGameModal
            onClose={handleCloseExitModal}
            onExit={handleExitGame}
          />
        </Suspense>
      )}
    </section>
  )
}

export default MemoramaGame
